import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import DateRangePicker from "../components/DateRangePicker";
import ChannelSelector from "../components/ChannelSelector";
import NetworkGraph from "../components/NetworkGraph";
import TooltipUI from "../components/Tooltip";
import InfoIcon from "../components/InfoIcon";
import { shouldBypassSlackConnection } from "../utils/config";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";
import SyncButtonWithTimestamp from '../components/SyncButtonWithTimestamp';
import EmptyState from '../components/EmptyState';

// Import Slack data service functions
import {
  fetchOrganization,
  checkSlackConnection,
  clearCache
} from "../utils/slackDataService";
import { getApiBaseUrl } from "../utils/apiConfig";

// Import report service functions
import {
  fetchReport
} from "../utils/slackReportService";

const NetworkAnalysis = () => {
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [slackConnected, setSlackConnected] = useState(false);
  const [availableChannels, setAvailableChannels] = useState([]);
  const [noDataAvailable, setNoDataAvailable] = useState(false);
  
  // Initialize dateRange with "last30days"
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - 29);
    const endDate = new Date(today);
    return { startDate, endDate, range: "last30days" };
  });
  
  const [selectedChannels, setSelectedChannels] = useState([]);

  // Network analysis data states
  const [networkData, setNetworkData] = useState({ nodes: [], links: [] });
  const [networkMetrics, setNetworkMetrics] = useState({
    networkDensity: 0,
    centralityScores: [],
    siloIndex: 0
  });
  
  // Network density and silo index time series data
  const [networkDensityData, setNetworkDensityData] = useState([]);
  const [siloIndexData, setSiloIndexData] = useState([]);
  // Add bottleneck index data state
  const [bottleneckIndexData, setBottleneckIndexData] = useState([]);
  // Cluster stats from NetworkGraph community detection
  const [clusterStats, setClusterStats] = useState([]);
  // Right panel tab: "organization" | "clusters" | "teams"
  const [activeTab, setActiveTab] = useState("organization");
  // Cluster highlighted from the panel (communityId or null)
  const [highlightedClusterId, setHighlightedClusterId] = useState(null);
  // Teams (user groups) data from directory API
  const [memberMap, setMemberMap] = useState(null);
  const [userGroups, setUserGroups] = useState([]);
  const [teamStats, setTeamStats] = useState([]);
  const [highlightedTeamId, setHighlightedTeamId] = useState(null);
  const [alignmentScore, setAlignmentScore] = useState(null);

  // Check user session and load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      // Retrieve user data from localStorage
      const userData = localStorage.getItem("user");
      if (userData && userData !== "undefined") {
        try {
          setLoading(true);
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
          
          if (parsedUser.organizations && parsedUser.organizations.length > 0) {
            const firstOrgId = parsedUser.organizations[0].organizationId;
            const org = await fetchOrganization(firstOrgId);
            setOrganization(org);
            setOrganizations([org]);
          }
        } catch (error) {
          console.error("Error parsing user data:", error);
          setError("Error loading user data. Please try again.");
        } finally {
          setLoading(false);
        }
      } else {
        navigate("/login");
      }
    };
    
    loadInitialData();
  }, [navigate]);
  
  // Check Slack connection and load Slack data
  useEffect(() => {
    const loadSlackData = async () => {
      if (!organization) return;
     
      try {
        setLoading(true);
        setError(null);
        
        // If we should bypass, set slackConnected to true and proceed
        if (shouldBypassSlackConnection()) {
          console.log('NetworkAnalysis.jsx: Bypassing Slack check, setting slackConnected=true');
          setSlackConnected(true);
          // Don't fetch channels or users from Slack API - we'll get them from the report data
          console.log('NetworkAnalysis.jsx: Skipping fetchSlackChannels and fetchSlackUsers - will get all data from report');
        } else {
          // Original logic for production
          const isConnected = await checkSlackConnection(organization);
          setSlackConnected(isConnected);
          
          if (isConnected) {
            // Don't fetch channels or users from Slack API - we'll get them from the report data
            console.log('NetworkAnalysis.jsx: Skipping fetchSlackChannels and fetchSlackUsers - will get all data from report');
            setSlackConnected(true);
          } else {
            setError("Slack is not connected. Please connect Slack in the Integrations page.");
          }
        }
      } catch (error) {
        console.error("Error loading Slack data:", error);
        if (!shouldBypassSlackConnection()) {
          setError("Error checking Slack connection. Please try again.");
        }
      } finally {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    };
    
    loadSlackData();
  }, [organization]);

  // Fetch directory data (members + user groups) once org is available
  useEffect(() => {
    if (!organization) return;
    const orgId = organization._id || organization.id;
    if (!orgId) return;

    const loadDirectory = async () => {
      try {
        const token = localStorage.getItem("token");
        const apiUrl = getApiBaseUrl();
        const res = await fetch(`${apiUrl}/integrations/slack/directory/${orgId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data.members && Array.isArray(data.members)) {
          const map = new Map();
          data.members.forEach(m => map.set(m.slackUserId, m));
          setMemberMap(map);
        }
        if (data.userGroups && Array.isArray(data.userGroups)) {
          setUserGroups(data.userGroups);
        }
      } catch (err) {
        console.warn("Failed to load directory data:", err.message);
      }
    };
    loadDirectory();
  }, [organization]);

  // Load network data when slackConnected becomes true
  useEffect(() => {
    if (slackConnected && organization && initialLoadComplete) {
      loadNetworkData(false);
    }
  }, [slackConnected, organization, initialLoadComplete]);

  // Load network analysis data based on filters
  const loadNetworkData = async (isFilterChange = true, channelsOverride = null) => {
    // Make sure this check is robust
    if (!organization || !slackConnected) {
      console.log("Cannot load data: organization or slack not connected. Org:", organization, "Slack Connected:", slackConnected);
      return;
    }
    
    try {
      if (isFilterChange) {
        setDataLoading(true);
      } else {
        setLoading(true);
      }
      setError(null);
      setNoDataAvailable(false);
      
      // Force clear the cache when loading fresh data with filters
      clearCache();
      
      // Use channels override if provided, otherwise use state
      const channelsToUse = channelsOverride !== null ? channelsOverride : selectedChannels;
      
      console.log("Loading network data with filter:", {
        dateRange,
        selectedChannels: channelsToUse,
        isFilterChange
      });

      // Ensure we have valid date objects
      const startDate = new Date(dateRange.startDate);
      const endDate = new Date(dateRange.endDate);
      
      // Create date range object for slackDataService functions
      const currentDateRange = {
        startDate: startDate,
        endDate: endDate,
        range: dateRange.range
      };
      
      // Use fetchReport to get network data from the database (no filtering to avoid Slack API calls)
      console.log('Fetching network data using report approach...');
      
      const reportResult = await fetchReport(organization, {
        // Don't pass channels or includeFiltering to get full network data
        // Network analysis needs full organization data to be meaningful
      });
      
      // Extract network data from the report
      let networkDataResult = null;
      let networkMetricsResult = null;
      let timeSeriesResult = null;
      let userMetrics = new Map();  // Declare userMetrics in broader scope
      
      if (reportResult && reportResult.success && reportResult.data && reportResult.data.reportData?.network) {
        const networkData = reportResult.data.reportData.network;
        console.log("Network data from report:", networkData);
        console.log("Available network keys:", Object.keys(networkData));
        
        // Extract channel information from report metadata (only do this once)
        if (reportResult.data.channelMetadata && availableChannels.length === 0) {
          const channels = Object.values(reportResult.data.channelMetadata).map(metadata => ({
            id: metadata.channelId,
            name: metadata.name,
            is_private: metadata.is_private,
            num_members: metadata.num_members,
            topic: { value: metadata.topic },
            creator: metadata.creator,
            created: metadata.created
          }));
          console.log(`Extracted ${channels.length} channels from report metadata`);
          setAvailableChannels(channels);
        }
        
        // Get all dates within the selected range
        const allDates = Object.keys(networkData.networkGraph || {}).sort();
        const datesInRange = allDates.filter(dateStr => {
          const datePart = dateStr.split(',')[0].trim();
          const dateObj = new Date(datePart);
          return dateObj >= startDate && dateObj <= endDate;
        });
        
        console.log("Available network graph dates:", allDates);
        console.log("Dates in selected range:", datesInRange);
        
        if (datesInRange.length === 0) {
          setNoDataAvailable(true);
          return;
        }
        
        // Aggregate network graph across all days in range
        const aggregatedGraph = { nodes: new Map(), links: new Map() };
        // userMetrics is already declared above
        
        // Get channel filtering info
        // const channelsToFilter = channelsOverride || selectedChannels;
        // const shouldFilterByChannels = channelsToFilter && channelsToFilter.length > 0;
        
        // console.log("Channel filtering:", {
        //   channelsToFilter,
        //   shouldFilterByChannels,
        //   availableChannels: availableChannels.map(c => c.name)
        // });
        
        datesInRange.forEach(dateStr => {
          const dailyGraph = networkData.networkGraph[dateStr];
          const dailyMetrics = networkData.networkMetrics[dateStr];
          const dailyCentrality = networkData.centralityScores[dateStr] || [];
          // const dailyChannelConnectivity = networkData.channelConnectivity[dateStr] || {};
          
          // Filter by channels if specified
          let filteredNodes = dailyGraph.nodes;
          let filteredLinks = dailyGraph.links;
          let filteredCentrality = dailyCentrality;
          
          // if (shouldFilterByChannels) {
          //   // Get users active in selected channels
          //   const channelUserIds = new Set();
          //   channelsToFilter.forEach(channelName => {
          //     const channelData = dailyChannelConnectivity[channelName];
          //     if (channelData && channelData.users) {
          //       channelData.users.forEach(user => {
          //       channelUserIds.add(user.userId);
          //     });
          //   });
          //   
          //   // Filter nodes to only include users from selected channels
          //   filteredNodes = dailyGraph.nodes.filter(node => channelUserIds.has(node.id));
          //   const filteredNodeIds = new Set(filteredNodes.map(n => n.id));
          //   
          //   // Filter links to only include connections between filtered users
          //   filteredLinks = dailyGraph.links.filter(link => 
          //     filteredNodeIds.has(link.source) && filteredNodeIds.has(link.target)
          //   );
          //   
          //   // Filter centrality scores to only include users from selected channels
          //   filteredCentrality = dailyCentrality.filter(score => channelUserIds.has(score.userId));
          // }
          
          // Aggregate nodes (including message counts for tooltips)
          filteredNodes.forEach(node => {
            if (!aggregatedGraph.nodes.has(node.id)) {
              aggregatedGraph.nodes.set(node.id, {
                ...node,
                value: 0,
                channelActivity: {
                  ...(node.channelActivity || {}),
                  publicMessages: 0,
                  privateMessages: 0,
                  publicInteractions: 0,
                  privateInteractions: 0,
                },
              });
            }

            const aggNode = aggregatedGraph.nodes.get(node.id);
            aggNode.value += node.value || 1;

            const ca = node.channelActivity || {};
            if (ca.publicMessages != null) aggNode.channelActivity.publicMessages += Number(ca.publicMessages) || 0;
            if (ca.privateMessages != null) aggNode.channelActivity.privateMessages += Number(ca.privateMessages) || 0;
            if (ca.publicInteractions != null) aggNode.channelActivity.publicInteractions += Number(ca.publicInteractions) || 0;
            if (ca.privateInteractions != null) aggNode.channelActivity.privateInteractions += Number(ca.privateInteractions) || 0;

            // Keep other (non-aggregated) properties from the latest day in range
            // so fields like centrality and publicIndex remain available.
            Object.keys(node).forEach((k) => {
              if (k === 'id' || k === 'value' || k === 'channelActivity') return;
              aggNode[k] = node[k];
            });
          });
          
          // Aggregate links
          filteredLinks.forEach(link => {
            const key = `${link.source}-${link.target}`;
            if (!aggregatedGraph.links.has(key)) {
              aggregatedGraph.links.set(key, { ...link, value: 0 });
            }
            aggregatedGraph.links.get(key).value += link.value || 1;
          });
          
          // Aggregate user metrics
          filteredCentrality.forEach(score => {
            if (!userMetrics.has(score.userId)) {
              userMetrics.set(score.userId, { ...score, count: 0 });
            }
            const agg = userMetrics.get(score.userId);
            agg.degreeCentrality = (agg.degreeCentrality || 0) + (score.degreeCentrality || 0);
            agg.betweennessCentrality = (agg.betweennessCentrality || 0) + (score.betweennessCentrality || 0);
            agg.closenessCentrality = (agg.closenessCentrality || 0) + (score.closenessCentrality || 0);
            agg.eigenvectorCentrality = (agg.eigenvectorCentrality || 0) + (score.eigenvectorCentrality || 0);
            agg.count++;
          });
        });
        
        // Average the user metrics
        userMetrics.forEach(agg => {
          const count = agg.count || 1;  // Avoid division by zero
          agg.degreeCentrality = (agg.degreeCentrality || 0) / count;
          agg.betweennessCentrality = (agg.betweennessCentrality || 0) / count;
          agg.closenessCentrality = (agg.closenessCentrality || 0) / count;
          agg.eigenvectorCentrality = (agg.eigenvectorCentrality || 0) / count;
          delete agg.count;
        });
        
        // Prepare final graph data
        networkDataResult = {
          nodes: Array.from(aggregatedGraph.nodes.values()),
          links: Array.from(aggregatedGraph.links.values())
        };
        console.log("Aggregated network graph:", networkDataResult);
        
        // Aggregate network metrics
        networkMetricsResult = {
          networkDensity: 0,
          siloIndex: 0,
          networkCentralization: 0,
          avgClusteringCoefficient: 0,
          bottleneckIndex: 0
        };
        
        let dayCount = datesInRange.length || 1;  // Avoid division by zero
        datesInRange.forEach(dateStr => {
          let dailyMetrics = networkData.networkMetrics[dateStr] || {};
          
          // If filtering by channels, use channel-specific metrics if available
          // if (shouldFilterByChannels) {
          //   const dailyChannelConnectivity = networkData.channelConnectivity[dateStr] || {};
          //   let channelSpecificMetrics = null;
          //   
          //   // Try to get channel-specific metrics
          //   channelsToFilter.forEach(channelName => {
          //     const channelData = dailyChannelConnectivity[channelName];
          //     if (channelData && channelData.metrics) {
          //       if (!channelSpecificMetrics) {
          //       channelSpecificMetrics = { ...channelData.metrics };
          //     } else {
          //       // If multiple channels, average the metrics
          //       Object.keys(channelSpecificMetrics).forEach(key => {
          //         channelSpecificMetrics[key] = (channelSpecificMetrics[key] + channelData.metrics[key]) / 2;
          //       });
          //     }
          //   }
          //   });
          //   
          //   // Use channel-specific metrics if available, otherwise use filtered graph to calculate
          //   if (channelSpecificMetrics) {
          //     dailyMetrics = channelSpecificMetrics;
          //   } else {
          //     // Calculate metrics based on filtered graph
          //     const filteredGraph = aggregatedGraph;
          //     const nodeCount = filteredGraph.nodes.size;
          //     const linkCount = filteredGraph.links.size;
          //     
          //     if (nodeCount > 1) {
          //       dailyMetrics.networkDensity = (2 * linkCount) / (nodeCount * (nodeCount - 1));
          //       // For other metrics, we'll use the original values but they'll be affected by the filtering
          //     }
          //   }
          // }
          
          Object.keys(networkMetricsResult).forEach(key => {
            networkMetricsResult[key] += dailyMetrics[key] || 0;
          });
        });
        
        Object.keys(networkMetricsResult).forEach(key => {
          networkMetricsResult[key] /= dayCount;
        });
        console.log("Aggregated network metrics:", networkMetricsResult);
        
        // Use existing time series processing
        const startDateStr = startDate.toISOString().split('T')[0];
        const endDateStr = endDate.toISOString().split('T')[0];
        
        const networkDensityData = [];
        const siloIndexData = [];
        const networkCentralizationData = [];
        const bottleneckIndexData = [];
        
        // Process each date in the report
        const MONTH_NUMS = { Jan:0, Feb:1, Mar:2, Apr:3, May:4, Jun:5, Jul:6, Aug:7, Sep:8, Oct:9, Nov:10, Dec:11 };
        Object.entries(networkData.networkMetrics || {}).forEach(([dateStr, metrics]) => {
          const datePart = dateStr.split(',')[0].trim();
          const [mName, mDay, mYear] = datePart.split(' ');
          const monthIdx = MONTH_NUMS[mName];
          const dayNum = parseInt(mDay);
          const yearNum = parseInt(mYear);
          const dateObj = new Date(yearNum, monthIdx, dayNum);
          const isoDate = `${yearNum}-${String(monthIdx + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
          
          // Check if this date is in the filter range
          if (dateObj >= startDate && dateObj <= endDate) {
            // Check if values are already in percentage form (0-100) or decimal form (0-1)
            const scalingFactor = (metrics.networkDensity > 1 || metrics.siloIndex > 1) ? 1 : 100;

            networkDensityData.push({
              date: isoDate,
              value: (metrics.networkDensity * scalingFactor) || 0
            });

            // Pull the daily graph for this date so we can recompute metrics that the
            // backend stores as 0 (e.g. when the bot has no private channel access).
            const dailyGraph = networkData.networkGraph?.[dateStr];

            // --- Silo index: avg privateRatio (publicIndex) across nodes ---
            let siloValue = metrics.siloIndex * scalingFactor;
            if (siloValue === 0 && dailyGraph?.nodes?.length > 0) {
              const avgPrivRatio = dailyGraph.nodes.reduce((s, n) => s + (n.publicIndex || 0), 0) / dailyGraph.nodes.length;
              siloValue = Math.round(avgPrivRatio * 1000) / 10;
            }
            siloIndexData.push({ date: isoDate, value: siloValue });

            // --- Network centralization: Freeman degree centralization ---
            let centralizationValue = metrics.networkCentralization * scalingFactor;
            if (centralizationValue === 0 && dailyGraph?.nodes?.length > 2 && dailyGraph?.links?.length > 0) {
              const degMap = new Map();
              dailyGraph.nodes.forEach(n => degMap.set(n.id ?? n, 0));
              dailyGraph.links.forEach(l => {
                const s = typeof l.source === 'object' ? l.source.id : l.source;
                const t = typeof l.target === 'object' ? l.target.id : l.target;
                degMap.set(s, (degMap.get(s) || 0) + 1);
                degMap.set(t, (degMap.get(t) || 0) + 1);
              });
              const degrees = Array.from(degMap.values());
              const maxDeg = Math.max(...degrees);
              const nn = degrees.length;
              if (maxDeg > 0 && nn > 2) {
                const sumDiff = degrees.reduce((s, d) => s + (maxDeg - d), 0);
                centralizationValue = Math.round(sumDiff / ((nn - 1) * (nn - 2)) * 1000) / 10;
              }
            }
            networkCentralizationData.push({ date: isoDate, value: centralizationValue });

            // --- Bottleneck index: top-10% betweenness share ---
            let bottleneckValue = (metrics.bottleneckIndex ?? 0) * scalingFactor;
            if (bottleneckValue === 0 && dailyGraph?.nodes?.length > 0) {
              const bwVals = dailyGraph.nodes.map(n => n.betweennessCentrality || 0);
              const totalBw = bwVals.reduce((s, v) => s + v, 0);
              if (totalBw > 0) {
                const topK = Math.max(1, Math.ceil(dailyGraph.nodes.length * 0.1));
                const topSum = [...bwVals].sort((a, b) => b - a).slice(0, topK).reduce((s, v) => s + v, 0);
                bottleneckValue = Math.round(topSum / totalBw * 1000) / 10;
              }
            }
            bottleneckIndexData.push({ date: isoDate, value: bottleneckValue });
          }
        });
        
        // Recompute headline aggregate values from the (possibly enriched) time-series
        const tsAvg = (arr) => arr.length > 0 ? arr.reduce((s, d) => s + d.value, 0) / arr.length : 0;
        if (siloIndexData.length > 0) networkMetricsResult.siloIndex = tsAvg(siloIndexData);
        if (networkCentralizationData.length > 0) networkMetricsResult.networkCentralization = tsAvg(networkCentralizationData);
        if (bottleneckIndexData.length > 0) networkMetricsResult.bottleneckIndex = tsAvg(bottleneckIndexData);

        timeSeriesResult = {
          networkDensityData,
          siloIndexData,
          networkCentralizationData,
          bottleneckIndexData
        };
      }
      
      // Check if data is null (no data available)
      if (!networkDataResult || !networkMetricsResult) {
        setNoDataAvailable(true);
        return;
      }
      
      setNetworkData(networkDataResult);

      // Use real time series data from report
      const { networkDensityData, siloIndexData, networkCentralizationData, bottleneckIndexData } = timeSeriesResult;
      
      // Set the real time series data directly
      setNetworkDensityData(networkDensityData);
      setSiloIndexData(siloIndexData);
      setBottleneckIndexData(bottleneckIndexData);
      
      // Log raw metrics before scaling
      console.log("Raw network metrics before scaling:", {
        networkDensity: networkMetricsResult.networkDensity,
        siloIndex: networkMetricsResult.siloIndex,
        networkCentralization: networkMetricsResult.networkCentralization,
        avgClusteringCoefficient: networkMetricsResult.avgClusteringCoefficient,
        bottleneckIndex: networkMetricsResult.bottleneckIndex
      });
      
      // Update metrics with aggregated data
      // No need for scaling check since we already scaled daily values
      const scalingFactor = 1; // Since daily are already scaled
      console.log("Using scaling factor:", scalingFactor);
      
      setNetworkMetrics({
        networkDensity: networkMetricsResult.networkDensity,
        siloIndex: networkMetricsResult.siloIndex,
        networkCentralization: networkMetricsResult.networkCentralization,
        avgClusteringCoefficient: networkMetricsResult.avgClusteringCoefficient,
        bottleneckIndex: networkMetricsResult.bottleneckIndex,
        centralityScores: Array.from(userMetrics.values()),
        // Store centralization data for the chart
        networkCentralizationData: networkCentralizationData,
        // Store bottleneck index data for the chart
        bottleneckIndexData: bottleneckIndexData
      });

      console.log('Network analysis loaded:', {
        nodes: networkDataResult.nodes?.length || 0,
        links: networkDataResult.links?.length || 0,
        density: networkMetricsResult.networkDensity || 0,
        silo: networkMetricsResult.siloIndex || 0,
        timeSeriesPoints: networkDensityData.length,
        hasRealData: networkDensityData.length > 0
      });

    } catch (error) {
      console.error("Error loading network data:", error);
      setNoDataAvailable(true);
    } finally {
      setDataLoading(false);
      setLoading(false);
    }
  };

  // Handle date range change
  const handleDateRangeChange = (newDateRange) => {
    console.log("Date range changed:", newDateRange);

    // Validate the date range before proceeding
    if (!newDateRange || !newDateRange.startDate || !newDateRange.endDate) {
      console.warn("Invalid date range received:", newDateRange);
      return;
    }
    
    setDateRange(newDateRange);
    // Don't call loadNetworkData here - let useEffect handle it
  };
  
  // Effect to load data when dateRange changes
  useEffect(() => {
    if (dateRange && dateRange.startDate && dateRange.endDate && initialLoadComplete) {
      console.log("Date range effect triggered, loading network data...");
      loadNetworkData(true);
    }
  }, [dateRange]);
  
  // Handle channel selection change
  // const handleChannelChange = (channels) => {
  //   setSelectedChannels(channels);
  //   console.log("Channel selection changed:", channels);
  //   // Trigger data reload with new channel filter
  //   if (dateRange && dateRange.startDate && dateRange.endDate) {
  //     loadNetworkData(true, channels);
  //   }
  // };

  // Custom tooltip for charts (valueLabel matches each metric, e.g. network density on dashboard)
  const CustomTooltip = ({ active, payload, label, valueLabel = 'Value' }) => {
    if (active && payload && payload.length && label) {
      const [year, month, day] = String(label).split('-').map(Number);
      const date = new Date(year, month - 1, day);
      const formattedDate = date.toLocaleDateString('en-US', { 
        weekday: 'short',
        month: 'short', 
        day: 'numeric'
      });
      
      return (
        <div className="bg-white-f p-3 border border-grey-20 rounded-md shadow-subtle">
          <p className="text-sm font-medium text-black-0">{formattedDate}</p>
          <p className="text-sm text-grey-60">
            {valueLabel}: <span className="font-medium text-black-0">{Math.round(payload[0].value)}%</span>
          </p>
        </div>
      );
    }
    return null;
  };

  // Format date for display (e.g., "Jan 1" or "Feb 15")
  const formatDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return `${date.toLocaleString('default', { month: 'short' })} ${day}`;
  };

  // Info icon SVG
  const InfoIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-info-circle ml-2 text-grey-60 cursor-help">
      <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
      <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0" />
      <path d="M12 9h.01" />
      <path d="M11 12h1v4h1" />
    </svg>
  );

  // Generate avatar with first letter of name
  const UserAvatar = ({ name }) => {
    const firstLetter = name ? name.charAt(0).toUpperCase() : '?';
    
    // Generate consistent color based on name
    const getAvatarColor = (name) => {
      const colors = [
        'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-red-500', 
        'bg-yellow-500', 'bg-indigo-500', 'bg-pink-500', 'bg-teal-500'
      ];
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }
      return colors[Math.abs(hash) % colors.length];
    };

    return (
      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium text-white ${getAvatarColor(name)}`}>
        {firstLetter}
      </div>
    );
  };

  // Show loading indicator for the entire page during initial load
  if (loading && !initialLoadComplete) {
    return (
      <div className="flex h-screen h-dvh overflow-hidden bg-grey-10">
        <Sidebar user={user} organization={organization} organizations={organizations} />
        <div className="flex-1 p-6 flex items-center justify-center">
          <div className="text-center">
            <div className="w-10 h-10 border-4 border-grey-40 border-t-brand rounded-full animate-spin inline-block mb-4"></div>
            <p className="text-grey-60">Loading network data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Calculate average score from time series data
  const calculateAverage = (data) => {
    if (!data || data.length === 0) return 0;
    const sum = data.reduce((total, item) => total + item.value, 0);
    return Math.round(sum / data.length);
  };

  // Format a percentage value: show 1 decimal when < 1 to avoid showing "0%" for e.g. 0.4%
  const fmtPct = (val) => {
    if (typeof val !== 'number' || isNaN(val)) return '0';
    if (val > 0 && val < 1) return val.toFixed(1);
    return Math.round(val).toString();
  };

  // Get qualitative level based on numeric score (0-100)
  const getMetricLevel = (score) => {
    if (score >= 80) return "Very high";
    if (score >= 60) return "High";
    if (score >= 40) return "Medium";
    if (score >= 20) return "Low";
    return "Very low";
  };

  // Map level to badge color (same palette as Activity column)
  const getMetricBadgeColor = (level) => {
    switch (level.toLowerCase()) {
      case 'high':
      case 'very high':
        return 'bg-[#DAFBEB] text-black-0';
      case 'medium':
        return 'bg-[#FBF0DA] text-black-0';
      case 'low':
      case 'very low':
        return 'bg-[#FBDADA] text-black-0';
      default:
        return 'bg-grey-20 text-grey-60';
    }
  };

  const buildStructuralGaps = () => {
    const links = Array.isArray(networkData?.links) ? networkData.links : [];
    if (links.length === 0) return [];

    const hasTeams = Array.isArray(teamStats) && teamStats.length > 1;
    const sourceItems = hasTeams
      ? teamStats
          .filter(team => Array.isArray(team._nodeIds) && team._nodeIds.length > 0)
          .map(team => ({
            id: String(team.usergroupId),
            name: team.name || team.handle || "Team",
            nodeIds: team._nodeIds,
            kind: "team",
          }))
      : (Array.isArray(clusterStats) ? clusterStats : [])
          .filter(cluster => Array.isArray(cluster._nodeIds) && cluster._nodeIds.length > 0)
          .map((cluster, index) => ({
            id: String(cluster.communityId),
            name: `Cluster ${index + 1}`,
            nodeIds: cluster._nodeIds,
            kind: "cluster",
          }));

    if (sourceItems.length < 2) return [];

    const normalizedLinks = links.map(link => ({
      source: typeof link.source === "object" ? link.source?.id : link.source,
      target: typeof link.target === "object" ? link.target?.id : link.target,
    }));

    const pairStats = [];
    for (let i = 0; i < sourceItems.length; i++) {
      for (let j = i + 1; j < sourceItems.length; j++) {
        const left = sourceItems[i];
        const right = sourceItems[j];
        const leftSet = new Set(left.nodeIds);
        const rightSet = new Set(right.nodeIds);
        const possibleEdges = leftSet.size * rightSet.size;
        if (possibleEdges === 0) continue;

        let crossEdges = 0;
        normalizedLinks.forEach(link => {
          if (!link.source || !link.target) return;
          const srcInLeft = leftSet.has(link.source);
          const srcInRight = rightSet.has(link.source);
          const trgInLeft = leftSet.has(link.target);
          const trgInRight = rightSet.has(link.target);
          if ((srcInLeft && trgInRight) || (srcInRight && trgInLeft)) {
            crossEdges += 1;
          }
        });

        const gapPct = Math.round((1 - Math.min(1, crossEdges / possibleEdges)) * 100);
        pairStats.push({
          id: `${left.kind}-${left.id}__${right.kind}-${right.id}`,
          leftName: left.name,
          rightName: right.name,
          possibleEdges,
          crossEdges,
          gapPct,
        });
      }
    }

    if (pairStats.length === 0) return [];

    const disconnectedPairs = pairStats
      .filter(pair => pair.crossEdges === 0)
      .sort((a, b) => b.possibleEdges - a.possibleEdges);

    if (disconnectedPairs.length > 0) {
      return disconnectedPairs.slice(0, 3);
    }

    return pairStats
      .sort((a, b) => {
        if (b.gapPct !== a.gapPct) return b.gapPct - a.gapPct;
        return b.possibleEdges - a.possibleEdges;
      })
      .slice(0, 3);
  };

  const structuralGaps = buildStructuralGaps();
  const structuralGapMode = (teamStats && teamStats.length > 1) ? "teams" : "clusters";

  return (
    <div className="flex h-screen h-dvh overflow-hidden bg-bg-gradient-dash">
      <Sidebar user={user} organization={organization} organizations={organizations} />
      
      <div className="flex-1  overflow-y-auto">
        {/* Header */}
        <div className="p-5 pb-0">
          <h1 className="text-xl font-normal text-black-0">Organizational network analysis</h1>
        </div>
        
        {/* Slack not connected message */}
        {!slackConnected && !loading && (
          <div className="bg-grey-20 p-4 rounded-md my-4 mx-5">
            <p className="text-grey-60">
              You need to connect to Slack first. Please go to the{" "}
              <button 
                onClick={() => navigate('/settings')}
                className="text-brand hover:underline"
              >
                Settings page
              </button>{" "}
              to set up your Slack connection.
            </p>
          </div>
        )}
        
        
        {/* Main content - only show if Slack is connected and data is loaded */}
        {slackConnected && (
          <>
            {/* Filters */}
            <div className="flex items-center justify-between p-5">
              <div className="flex items-center gap-3">
                <DateRangePicker 
                  dateRange={dateRange} 
                  onChange={handleDateRangeChange}
                />
                {/* <div className="flex items-center gap-2">
                  <ChannelSelector 
                    availableChannels={availableChannels}
                    selectedChannels={selectedChannels}
                    onChange={handleChannelChange}
                    singleSelect={true}
                  />
                  {selectedChannels.length > 0 && (
                    <button
                      onClick={() => {
                        setSelectedChannels([]);
                      }}
                      className="text-sm text-grey-60 hover:text-brand hover:underline"
                    >
                      Reset to all channels
                    </button>
                  )}
                </div> */}
              </div>
              <SyncButtonWithTimestamp organization={organization} dateRange={dateRange} onSyncComplete={() => window.location.reload()} />
            </div>
            
            {/* Loading indicator for filter changes */}
            {dataLoading && (
              <div className="flex justify-center items-center py-4 mb-4">
                <div className="w-6 h-6 border-2 border-grey-40 border-t-brand rounded-full animate-spin mr-3"></div>
                <span className="text-grey-60">Updating data...</span>
              </div>
            )}

            {noDataAvailable ? (
              <div className="mx-5">
                <EmptyState
                  variant="no-data"
                  title="No network data available"
                  description="There are no Slack messages or interactions for the selected time period. Try selecting a different date range."
                  actionButton={null}
                />
              </div>
            ) : (
              <div className="flex gap-4 h-[calc(100vh-140px)] p-5 pt-0 pb-0">
                {/* Network Graph - Left Side */}
                <div className="flex-1 bg-white-f p-5 rounded-lg border border-grey-20 shadow-subtle flex flex-col min-h-0">
                  <div className="flex items-center gap-1 mb-4 flex-shrink-0">
                    <h3 className="text-sm font-normal text-black-0">Collaboration network</h3>
                    <TooltipUI
                      content="A map of collaboration based on message threads and @mentions. Dense clusters can indicate teams; highly central nodes can indicate bottlenecks."
                      placement="bottom"
                      align="start"
                    >
                      <span className="inline-flex cursor-help">
                        <InfoIcon />
                      </span>
                    </TooltipUI>
                  </div>
                  <div className="flex-1 w-full min-h-0">
                    <NetworkGraph
                      data={networkData}
                      showClusters={activeTab === "clusters"}
                      highlightedClusterId={highlightedClusterId}
                      onClusterStats={setClusterStats}
                      memberMap={memberMap}
                      showTeams={activeTab === "teams"}
                      userGroups={userGroups}
                      highlightedTeamId={highlightedTeamId}
                      onTeamStats={setTeamStats}
                      onAlignmentScore={setAlignmentScore}
                    />
                  </div>
                </div>
                
                {/* Side Panel - Right Side */}
                <div className="w-[400px] flex-shrink-0 bg-white-f rounded-lg border border-grey-20 shadow-subtle flex flex-col min-h-0">
                  {/* Tab switcher */}
                  <div className="border-b border-grey-20 px-5 py-4 flex-shrink-0">
                    <div className="bg-grey-10 flex items-center p-[2px] rounded-[10px]">
                      <button
                        type="button"
                        onClick={() => { setActiveTab("organization"); setHighlightedClusterId(null); setHighlightedTeamId(null); }}
                        className={`flex-1 h-[28px] flex items-center justify-center px-2 py-1 rounded-[8px] text-sm font-medium text-black-0 transition-all ${
                          activeTab === "organization"
                            ? "bg-white-f border border-grey-20 shadow-[0px_1px_1px_0px_rgba(28,27,34,0.1)]"
                            : ""
                        }`}
                      >
                        Organization
                      </button>
                      <button
                        type="button"
                        onClick={() => { setActiveTab("clusters"); setHighlightedClusterId(null); setHighlightedTeamId(null); }}
                        className={`flex-1 h-[28px] flex items-center justify-center px-2 py-1 rounded-[8px] text-sm font-medium text-black-0 transition-all ${
                          activeTab === "clusters"
                            ? "bg-white-f border border-grey-20 shadow-[0px_1px_1px_0px_rgba(28,27,34,0.1)]"
                            : ""
                        }`}
                      >
                        Clusters
                      </button>
                      <button
                        type="button"
                        onClick={() => { setActiveTab("teams"); setHighlightedClusterId(null); setHighlightedTeamId(null); }}
                        className={`flex-1 h-[28px] flex items-center justify-center px-2 py-1 rounded-[8px] text-sm font-medium text-black-0 transition-all ${
                          activeTab === "teams"
                            ? "bg-white-f border border-grey-20 shadow-[0px_1px_1px_0px_rgba(28,27,34,0.1)]"
                            : ""
                        }`}
                      >
                        Teams
                      </button>
                    </div>
                  </div>

                  {/* Organization tab content */}
                  {activeTab === "organization" && (
                    <div className="flex-1 overflow-auto min-h-0 px-5 space-y-6 pt-4">
                      {/* Network Density */}
                      <div>
                        <div className="flex items-center mb-2">
                          <h4 className="text-sm font-normal text-black-0">Network density</h4>
                          <div className="relative group">
                            <InfoIcon />
                            <div className="absolute left-0 top-full mt-2 hidden group-hover:block w-64 bg-white-f p-3 rounded-md border border-grey-20 shadow-md z-[9999] text-xs text-black-0">
                              Network density measures the percentage of potential connections that are actually realized in your organization's network. Higher values indicate stronger collaboration patterns across teams.
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mb-3">
                          <p className="font-medium text-[18px] text-black-0">
                            {fmtPct(networkMetrics.networkDensity)}%
                          </p>
                          <span className={`px-2 py-1 rounded-md text-xs font-normal ${getMetricBadgeColor(getMetricLevel(typeof networkMetrics.networkDensity === 'number' ? Math.round(networkMetrics.networkDensity) : 0))}`}>
                            {getMetricLevel(typeof networkMetrics.networkDensity === 'number' ? Math.round(networkMetrics.networkDensity) : 0)}
                          </span>
                        </div>
                        <div className="h-[100px]">
                          {networkDensityData && networkDensityData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={networkDensityData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F5" />
                                <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 10 }} stroke="#ABABBA" />
                                <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10 }} stroke="#ABABBA" />
                                <Tooltip content={<CustomTooltip valueLabel="Network density" />} />
                                <Line type="linear" dataKey="value" stroke="#31048B" strokeWidth={2} dot={false} />
                              </LineChart>
                            </ResponsiveContainer>
                          ) : (
                            <div className="flex items-center justify-center h-full">
                              <p className="text-xs text-grey-60">No data</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Silo Index */}
                      <div className="border-t border-grey-20 pt-6">
                        <div className="flex items-center mb-2">
                          <h4 className="text-sm font-normal text-black-0">Silo index</h4>
                          <div className="relative group">
                            <InfoIcon />
                            <div className="absolute left-0 top-full mt-2 hidden group-hover:block w-64 bg-white-f p-3 rounded-md border border-grey-20 shadow-md z-[9999] text-xs text-black-0">
                              Silo index measures the percentage of communication that happens in private channels vs public channels. Higher values indicate more private channel usage, which can create information silos.
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mb-3">
                          <p className="font-medium text-[18px] text-black-0">
                            {fmtPct(networkMetrics.siloIndex)}%
                          </p>
                          <span className={`px-2 py-1 rounded-md text-xs font-normal ${getMetricBadgeColor(getMetricLevel(typeof networkMetrics.siloIndex === 'number' ? Math.round(networkMetrics.siloIndex) : 0))}`}>
                            {getMetricLevel(typeof networkMetrics.siloIndex === 'number' ? Math.round(networkMetrics.siloIndex) : 0)}
                          </span>
                        </div>
                        <div className="h-[100px]">
                          {siloIndexData && siloIndexData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={siloIndexData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F5" />
                                <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 10 }} stroke="#ABABBA" />
                                <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10 }} stroke="#ABABBA" />
                                <Tooltip content={<CustomTooltip />} />
                                <Line type="linear" dataKey="value" stroke="#31048B" strokeWidth={2} dot={false} />
                              </LineChart>
                            </ResponsiveContainer>
                          ) : (
                            <div className="flex items-center justify-center h-full">
                              <p className="text-xs text-grey-60">No data</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Network Centralization */}
                      <div className="border-t border-grey-20 pt-6">
                        <div className="flex items-center mb-2">
                          <h4 className="text-sm font-normal text-black-0">Network centralization</h4>
                          <div className="relative group">
                            <InfoIcon />
                            <div className="absolute left-0 top-full mt-2 hidden group-hover:block w-64 bg-white-f p-3 rounded-md border border-grey-20 shadow-md z-[9999] text-xs text-black-0">
                              Network centralization measures the extent to which the network revolves around a few central nodes. Higher values indicate a more hierarchical organization.
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mb-3">
                          <p className="font-medium text-[18px] text-black-0">
                            {fmtPct(networkMetrics.networkCentralization)}%
                          </p>
                          <span className={`px-2 py-1 rounded-md text-xs font-normal ${getMetricBadgeColor(getMetricLevel(typeof networkMetrics.networkCentralization === 'number' ? Math.round(networkMetrics.networkCentralization) : 0))}`}>
                            {getMetricLevel(typeof networkMetrics.networkCentralization === 'number' ? Math.round(networkMetrics.networkCentralization) : 0)}
                          </span>
                        </div>
                        <div className="h-[100px]">
                          {networkMetrics.networkCentralizationData && networkMetrics.networkCentralizationData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={networkMetrics.networkCentralizationData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F5" />
                                <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 10 }} stroke="#ABABBA" />
                                <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10 }} stroke="#ABABBA" />
                                <Tooltip content={<CustomTooltip />} />
                                <Line type="linear" dataKey="value" stroke="#31048B" strokeWidth={2} dot={false} />
                              </LineChart>
                            </ResponsiveContainer>
                          ) : (
                            <div className="flex items-center justify-center h-full">
                              <p className="text-xs text-grey-60">No data</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Bottleneck Index */}
                      <div className="border-t border-grey-20 pt-6">
                        <div className="flex items-center mb-2">
                          <h4 className="text-sm font-normal text-black-0">Bottleneck index</h4>
                          <div className="relative group">
                            <InfoIcon />
                            <div className="absolute left-0 top-full mt-2 hidden group-hover:block w-64 bg-white-f p-3 rounded-md border border-grey-20 shadow-md z-[9999] text-xs text-black-0">
                              Bottleneck index measures how dependent the organization is on a few key nodes for information flow. Higher values indicate more potential bottlenecks.
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mb-3">
                          <p className="font-medium text-[18px] text-black-0">
                            {fmtPct(typeof networkMetrics.bottleneckIndex === 'number' ? networkMetrics.bottleneckIndex : calculateAverage(bottleneckIndexData))}%
                          </p>
                          <span className={`px-2 py-1 rounded-md text-xs font-normal ${getMetricBadgeColor(getMetricLevel(typeof networkMetrics.bottleneckIndex === 'number' ? Math.round(networkMetrics.bottleneckIndex) : calculateAverage(bottleneckIndexData)))}`}>
                            {getMetricLevel(typeof networkMetrics.bottleneckIndex === 'number' ? Math.round(networkMetrics.bottleneckIndex) : calculateAverage(bottleneckIndexData))}
                          </span>
                        </div>
                        <div className="h-[100px]">
                          {bottleneckIndexData && bottleneckIndexData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={bottleneckIndexData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F5" />
                                <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 10 }} stroke="#ABABBA" />
                                <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10 }} stroke="#ABABBA" />
                                <Tooltip content={<CustomTooltip />} />
                                <Line type="linear" dataKey="value" stroke="#31048B" strokeWidth={2} dot={false} />
                              </LineChart>
                            </ResponsiveContainer>
                          ) : (
                            <div className="flex items-center justify-center h-full">
                              <p className="text-xs text-grey-60">No data</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Structural Gaps */}
                      <div className="border-t border-grey-20 pt-6 pb-6">
                        <div className="flex items-center mb-4">
                          <h4 className="text-sm font-medium text-black-0">Structural gaps</h4>
                          <div className="relative group ml-1">
                            <InfoIcon />
                            <div className="absolute left-0 top-full mt-2 hidden group-hover:block w-64 bg-white-f p-3 rounded-md border border-grey-20 shadow-md z-[9999] text-xs text-black-0">
                              Structural gaps highlight pairs of groups with no (or minimal) direct collaboration links. User Groups are used when available; otherwise detected collaboration clusters are used.
                            </div>
                          </div>
                        </div>
                        <div className="space-y-3">
                          {structuralGaps.length > 0 ? (
                            structuralGaps.map((gap) => (
                              <div key={gap.id} className="flex justify-between items-center">
                                <div className="flex items-center gap-2">
                                  <div className="px-2 py-1 bg-grey-20 rounded text-xs font-normal">{gap.leftName}</div>
                                  <span className="text-xs text-grey-60">↔</span>
                                  <div className="px-2 py-1 bg-grey-20 rounded text-xs font-normal">{gap.rightName}</div>
                                </div>
                                <span className="text-sm font-normal text-grey-60">{gap.gapPct}% gap</span>
                              </div>
                            ))
                          ) : (
                            <div className="flex items-center justify-center h-16">
                              <p className="text-sm text-grey-60">
                                {structuralGapMode === "teams" ? "No structural gaps detected between user groups" : "No structural gaps detected between clusters"}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Team-Cluster Alignment */}
                      {teamStats.length > 0 && clusterStats.length > 0 && (
                        <div className="border-t border-grey-20 pt-6 pb-6">
                          <div className="flex items-center mb-2">
                            <h4 className="text-sm font-normal text-black-0">Team-Cluster alignment</h4>
                            <div className="relative group">
                              <InfoIcon />
                              <div className="absolute left-0 top-full mt-2 hidden group-hover:block w-64 bg-white-f p-3 rounded-md border border-grey-20 shadow-md z-[9999] text-xs text-black-0">
                                Measures how well formal teams (Slack User Groups) map to naturally-emerging collaboration clusters. High alignment means teams collaborate mostly within their formal boundaries; low alignment means organic collaboration cuts across formal teams.
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mb-3">
                            <p className="font-medium text-[18px] text-black-0">
                              {alignmentScore != null ? `${alignmentScore}%` : '--'}
                            </p>
                            {alignmentScore != null && (
                              <span className={`px-2 py-1 rounded-md text-xs font-normal ${getMetricBadgeColor(
                                alignmentScore >= 70 ? 'High' : alignmentScore >= 40 ? 'Medium' : 'Low'
                              )}`}>
                                {alignmentScore >= 70 ? 'High' : alignmentScore >= 40 ? 'Medium' : 'Low'}
                              </span>
                            )}
                          </div>
                          <div className="space-y-2">
                            {teamStats.slice(0, 3).map(team => {
                              if (!team.bestCluster) return null;
                              const clusterRank = clusterStats.findIndex(c => c.communityId === team.bestCluster.communityId);
                              const clusterLabel = clusterRank >= 0 ? `Cluster ${clusterRank + 1}` : 'Cluster';
                              const concLevel = team.bestCluster.jaccardPct >= 70 ? 'High' : team.bestCluster.jaccardPct >= 40 ? 'Medium' : 'Low';
                              return (
                                <div key={`align-${team.usergroupId}`} className="flex justify-between items-center">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-black-0">{team.name}</span>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-grey-60"><path d="M5 12l14 0"/><path d="M13 18l6 -6"/><path d="M13 6l6 6"/></svg>
                                    <span className="text-sm text-grey-60">{clusterLabel}</span>
                                  </div>
                                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getMetricBadgeColor(concLevel)}`}>
                                    {team.bestCluster.jaccardPct}%
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Clusters tab content */}
                  {activeTab === "clusters" && (
                    <div className="flex-1 overflow-auto min-h-0">
                      {clusterStats && clusterStats.length > 0 ? (
                        clusterStats.map((cluster, index) => {
                          const densityLevel = cluster.internalDensity >= 60 ? 'High' : cluster.internalDensity >= 30 ? 'Medium' : 'Low';
                          const outboundLevel = cluster.outboundRate >= 60 ? 'High' : cluster.outboundRate >= 30 ? 'Medium' : 'Low';
                          const privacyLevel = cluster.avgPrivacy >= 60 ? 'High' : cluster.avgPrivacy >= 30 ? 'Medium' : 'Low';
                          const isActive = highlightedClusterId === cluster.communityId;
                          return (
                            <div
                              key={`cluster-${cluster.communityId}`}
                              className={`border-b border-grey-20 flex flex-col gap-2 p-3 cursor-pointer transition-colors ${isActive ? 'bg-grey-10' : 'hover:bg-grey-10'}`}
                              onClick={() => setHighlightedClusterId(isActive ? null : cluster.communityId)}
                            >
                              <div className="flex items-baseline justify-between tracking-[-0.5px] w-full">
                                <p className="text-[16px] font-medium leading-6 text-black-0">Cluster {index + 1}</p>
                                <p className="text-xs font-normal leading-[22px] text-grey-60">{cluster.members} members</p>
                              </div>
                              <div className="flex flex-col gap-1">
                                <div className="flex gap-2 items-center w-full">
                                  <p className="flex-1 text-sm font-normal leading-6 text-black-0 tracking-[-0.5px]">Internal density</p>
                                  <p className="text-sm font-semibold leading-6 text-black-0 tracking-[-0.5px]">{cluster.internalDensity}%</p>
                                  <div className={`h-5 flex items-center justify-center px-2 rounded ${getMetricBadgeColor(densityLevel)}`}>
                                    <p className="text-xs font-medium leading-[22px] text-black-0">{densityLevel}</p>
                                  </div>
                                </div>
                                <div className="flex gap-2 items-center w-full">
                                  <p className="flex-1 text-sm font-normal leading-6 text-black-0 tracking-[-0.5px]">Outbound collaboration</p>
                                  <p className="text-sm font-semibold leading-6 text-black-0 tracking-[-0.5px]">{cluster.outboundRate}%</p>
                                  <div className={`h-5 flex items-center justify-center px-2 rounded ${getMetricBadgeColor(outboundLevel)}`}>
                                    <p className="text-xs font-medium leading-[22px] text-black-0">{outboundLevel}</p>
                                  </div>
                                </div>
                                <div className="flex gap-2 items-center w-full">
                                  <p className="flex-1 text-sm font-normal leading-6 text-black-0 tracking-[-0.5px]">Internal silo index</p>
                                  <p className="text-sm font-semibold leading-6 text-black-0 tracking-[-0.5px]">{cluster.avgPrivacy}%</p>
                                  <div className={`h-5 flex items-center justify-center px-2 rounded ${getMetricBadgeColor(privacyLevel)}`}>
                                    <p className="text-xs font-medium leading-[22px] text-black-0">{privacyLevel}</p>
                                  </div>
                                </div>
                              </div>
                              {cluster.roles?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {cluster.roles.slice(0, 5).map(r => (
                                    <span key={r.title} className="px-1.5 py-0.5 bg-grey-10 rounded text-[11px] text-grey-60">
                                      {r.title} ({r.count})
                                    </span>
                                  ))}
                                  {cluster.roles.length > 5 && (
                                    <span className="px-1.5 py-0.5 bg-grey-10 rounded text-[11px] text-grey-60">
                                      +{cluster.roles.length - 5} more roles
                                    </span>
                                  )}
                                </div>
                              )}
                              {cluster.bestTeam && (
                                <div className="flex items-center gap-1 mt-1 text-[11px] text-grey-60">
                                  <span>Best overlap:</span>
                                  <span className="font-medium text-black-0">{cluster.bestTeam.name}</span>
                                  <span>({cluster.bestTeam.pct}% of cluster, {cluster.bestTeam.sharedCount} shared)</span>
                                </div>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <div className="flex items-center justify-center h-32">
                          <p className="text-sm text-grey-60">No clusters detected</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Teams tab content */}
                  {activeTab === "teams" && (
                    <div className="flex-1 overflow-auto min-h-0">
                      {teamStats && teamStats.length > 0 ? (
                        teamStats.map((team) => {
                          const densityLevel = team.internalDensity >= 60 ? 'High' : team.internalDensity >= 30 ? 'Medium' : 'Low';
                          const outboundLevel = team.outboundRate >= 60 ? 'High' : team.outboundRate >= 30 ? 'Medium' : 'Low';
                          const privacyLevel = team.avgPrivacy >= 60 ? 'High' : team.avgPrivacy >= 30 ? 'Medium' : 'Low';
                          const isActive = highlightedTeamId === team.usergroupId;
                          const totalMembers = typeof team.totalMembers === 'number' ? team.totalMembers : team.members;
                          const hasInactiveMembers = totalMembers > team.members;
                          return (
                            <div
                              key={`team-${team.usergroupId}`}
                              className={`border-b border-grey-20 flex flex-col gap-2 p-3 cursor-pointer transition-colors ${isActive ? 'bg-grey-10' : 'hover:bg-grey-10'}`}
                              onClick={() => setHighlightedTeamId(isActive ? null : team.usergroupId)}
                            >
                              <div className="flex items-baseline justify-between tracking-[-0.5px] w-full">
                                <div className="flex items-baseline gap-2">
                                  <p className="text-[16px] font-medium leading-6 text-black-0">{team.name}</p>
                                  {team.handle && <p className="text-xs font-normal leading-[22px] text-grey-60">@{team.handle}</p>}
                                </div>
                                <p className="text-xs font-normal leading-[22px] text-grey-60">
                                  {hasInactiveMembers ? `${team.members} active / ${totalMembers} total` : `${team.members} members`}
                                </p>
                              </div>
                              <div className="flex flex-col gap-1">
                                <div className="flex gap-2 items-center w-full">
                                  <p className="flex-1 text-sm font-normal leading-6 text-black-0 tracking-[-0.5px]">Internal density</p>
                                  <p className="text-sm font-semibold leading-6 text-black-0 tracking-[-0.5px]">{team.internalDensity}%</p>
                                  <div className={`h-5 flex items-center justify-center px-2 rounded ${getMetricBadgeColor(densityLevel)}`}>
                                    <p className="text-xs font-medium leading-[22px] text-black-0">{densityLevel}</p>
                                  </div>
                                </div>
                                <div className="flex gap-2 items-center w-full">
                                  <p className="flex-1 text-sm font-normal leading-6 text-black-0 tracking-[-0.5px]">Outbound collaboration</p>
                                  <p className="text-sm font-semibold leading-6 text-black-0 tracking-[-0.5px]">{team.outboundRate}%</p>
                                  <div className={`h-5 flex items-center justify-center px-2 rounded ${getMetricBadgeColor(outboundLevel)}`}>
                                    <p className="text-xs font-medium leading-[22px] text-black-0">{outboundLevel}</p>
                                  </div>
                                </div>
                                <div className="flex gap-2 items-center w-full">
                                  <p className="flex-1 text-sm font-normal leading-6 text-black-0 tracking-[-0.5px]">Internal silo index</p>
                                  <p className="text-sm font-semibold leading-6 text-black-0 tracking-[-0.5px]">{team.avgPrivacy}%</p>
                                  <div className={`h-5 flex items-center justify-center px-2 rounded ${getMetricBadgeColor(privacyLevel)}`}>
                                    <p className="text-xs font-medium leading-[22px] text-black-0">{privacyLevel}</p>
                                  </div>
                                </div>
                              </div>
                              {team.roles?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {team.roles.slice(0, 5).map(r => (
                                    <span key={r.title} className="px-1.5 py-0.5 bg-grey-10 rounded text-[11px] text-grey-60">
                                      {r.title} ({r.count})
                                    </span>
                                  ))}
                                  {team.roles.length > 5 && (
                                    <span className="px-1.5 py-0.5 bg-grey-10 rounded text-[11px] text-grey-60">
                                      +{team.roles.length - 5} more roles
                                    </span>
                                  )}
                                </div>
                              )}
                              {hasInactiveMembers && (
                                <div className="text-[11px] text-grey-60">
                                  Metrics and roles are based on active members in selected date range.
                                </div>
                              )}
                              {team.bestCluster && (() => {
                                const clusterRank = clusterStats.findIndex(c => c.communityId === team.bestCluster.communityId);
                                const clusterLabel = clusterRank >= 0 ? `Cluster ${clusterRank + 1}` : 'Cluster';
                                return (
                                  <div className="flex items-center gap-1 mt-1 text-[11px] text-grey-60">
                                    <span>Best overlap:</span>
                                    <span className="font-medium text-black-0">{clusterLabel}</span>
                                    <span>({team.bestCluster.pct}% of team, {team.bestCluster.sharedCount} shared)</span>
                                  </div>
                                );
                              })()}
                            </div>
                          );
                        })
                      ) : (
                        <div className="flex items-center justify-center h-32">
                          <p className="text-sm text-grey-60">No user groups found</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NetworkAnalysis; 